/**
 * Timesheet `projectIds` is a delimited string of project ids, e.g.
 * "2|6|8|13|26" (see the timesheet API response).
 *
 * mergeProjectIds keeps the existing list intact and only appends the
 * user-selected project id when it is a valid id and not already present. It
 * never removes an existing id, so it is safe for both flows:
 *  - Add:    take whatever ids already exist, add the selected one if missing.
 *  - Update: preserve every existing id, add the selected one if missing.
 */

/** Delimiter used by the API for the `projectIds` string. */
export const PROJECT_IDS_DELIMITER = "|";

/**
 * A project id is always a positive integer. Validating the selected id before
 * it is added prevents unintended / malformed values from entering the list.
 */
export const isValidProjectId = (id: string | number | null | undefined): boolean =>
  /^\d+$/.test(String(id ?? "").trim());

export function mergeProjectIds(
  existing: string | number | null | undefined,
  selectedProjectId: string | number | null | undefined
): string {
  // Split the delimited string into trimmed, non-empty ids. Existing ids come
  // from the server and are preserved as-is (never removed).
  const ids = String(existing ?? "")
    .split(PROJECT_IDS_DELIMITER)
    .map((id) => id.trim())
    .filter((id) => id.length > 0);

  const selected = String(selectedProjectId ?? "").trim();

  // Add the selected id only when it is a valid project id and not already
  // present in the list.
  if (isValidProjectId(selected) && !ids.includes(selected)) {
    ids.push(selected);
  }

  return ids.join(PROJECT_IDS_DELIMITER);
}
